<template>
  <PageContent>
    <div class="grow p-1">
      <OdoscopeSearchHeader
        :headline="headline"
        :results-count="resultsCount"
        :has-filters="hasFilters"
        :is-loading="isLoading"
        :values="sortingValues"
        :filters="odoscopeSearch.unfilteredFilters.value"
        @reset-price-filter="odoscopeSearch.resetPriceFilter"
        @apply-attribute-filter="odoscopeSearch.applyAttributeFilter"
        @reset-filter="odoscopeSearch.resetFilter"
      />

      <OdoscopeSearchDesktopFilters
        v-if="!isMobileOrTablet && hasFilters"
        :key="`desktop-filters-${odoscopeSearch.filters.value?.length || 0}`"
        :filters="odoscopeSearch.filters.value"
        :applied-filter="odoscopeSearch.appliedFilter.value"
        :applied-boolean-values="appliedBooleanValues"
        :applied-attribute-values="appliedAttributeValues"
        :sorting-values="sortingValues"
        :unfiltered-filters="odoscopeSearch.unfilteredFilters.value"
        @apply-boolean-filter="odoscopeSearch.applyBooleanFilter"
        @apply-attribute-filter="odoscopeSearch.applyAttributeFilter"
        @apply-price-filter="odoscopeSearch.applyPriceFilter"
        @reset-filter="odoscopeSearch.resetFilter"
        @reset-price-filter="odoscopeSearch.resetPriceFilter"
      />

      <div v-if="!isMobileOrTablet" class="mb-2 flex max-w-full flex-col">
        <OdoscopeSearchActiveFilters
          v-if="
            activeFilters &&
            odoscopeSearch.unfilteredFilters.value &&
            !isMobileOrTablet
          "
          :filters="odoscopeSearch.unfilteredFilters.value"
          :active-filters="activeFilters"
          @reset-price-filter="odoscopeSearch.resetPriceFilter"
          @apply-attribute-filter="odoscopeSearch.applyAttributeFilter"
          @reset-filter="odoscopeSearch.resetFilter"
        />
      </div>
    </div>

    <NuxtLazyHydrate when-idle>
      <ProductList
        :loading="isLoading"
        :per-page="productsPerPage"
        :products="odoscopeSearch.products.value"
        :refreshing="isLoading"
        class="grid w-auto grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4"
        @click:product="trackProductClick"
      />
    </NuxtLazyHydrate>
    <Pagination
      v-if="odoscopeSearch.pagination.value"
      :current-page="odoscopeSearch.pagination.value.page"
      :first-page="odoscopeSearch.pagination.value.first"
      :last-page="odoscopeSearch.pagination.value.last"
    />

    <OdoscopeSearchFilterSlideIn
      v-if="hasFilters && filtersOpen"
      :key="`slide-in-filters-${odoscopeSearch.filters.value?.length || 0}`"
      :match-count="resultsCount"
      :applied-filter="odoscopeSearch.appliedFilter.value"
      :filters="odoscopeSearch.filters.value"
      :values="sortingValues"
      :unfiltered-filters="odoscopeSearch.unfilteredFilters.value"
      @apply-filters="odoscopeSearch.applyFilters"
    />
    <SuggestedCategories
      v-if="
        !odoscopeSearch.products.value || !odoscopeSearch.products.value.length
      "
    />
    <TopCategories v-else />
  </PageContent>
</template>

<script setup lang="ts">
import type { Product } from '@scayle/storefront-nuxt'
import useOdoscopeSearch from '~/composables/useOdoscopeSearch'
import useOdoscopeSearchAppliedFilters from '~/composables/useOdoscopeSearchAppliedFilters'
import { useDeviceType } from '~/composables/useDeviceType'
import { odoscopeSortingValues } from '#layers/base/utils/odoscopeSort'

useRouteCache((helper) => {
  helper.setMaxAge(3600).setCacheable()
})

/** Initialization */

const i18n = useI18n()
const route = useRoute()
const scope = effectScope()
const { isMobileOrTablet } = useDeviceType()

const odoscopeSearch = useOdoscopeSearch()
const { activeFilters } = useQueryFilterState()
const { appliedBooleanValues, appliedAttributeValues } =
  useOdoscopeSearchAppliedFilters()

const { isOpen: filtersOpen } = useSlideIn('OdoscopeFilterSlideIn')

const {
  trackSelectItem,
  trackViewSearchResult,
  trackViewItemList,
  mapProductToTrackingPayload,
} = useTrackingEvents()

const productsPerPage = ref(60)
const term = computed(() => (route.query.term || '') as string)

const sortingValues = Object.values(odoscopeSortingValues)
const resultsCount = computed(() => odoscopeSearch.resultsCount.value)
const hasFilters = computed(() => Boolean(odoscopeSearch.filters.value?.length))

const isLoading = computed(() => odoscopeSearch.status.value === 'pending')

const headline = computed(() =>
  i18n.t('search.result', {
    term: term.value,
    resultsCount: resultsCount.value,
  }),
)

/** Tracking */

const trackProductClick = (product: Product, index: number) => {
  trackSelectItem({
    product,
    index,
    listName: 'search results',
    listType: 'odoscope',
  })
}

/** Data fetching */

scope.run(() => {
  useWatch(
    () => route.query,
    async () => {
      await odoscopeSearch.fetchProducts()
      if (import.meta.client) {
        window.scrollTo({ top: 0 })
      }
    },
  )

  // Track view_search_result + view_item_list on initial product load
  const hasTrackedSearchResult = ref(false)
  useWatch(
    () => odoscopeSearch.products.value,
    (loadedProducts: Product[]) => {
      if (hasTrackedSearchResult.value) {
        return
      }
      if (
        !loadedProducts?.length &&
        odoscopeSearch.status.value !== 'success'
      ) {
        return
      }

      hasTrackedSearchResult.value = true

      const ecommerceItems = loadedProducts.map(
        (product: Product, index: number) => ({
          ...mapProductToTrackingPayload(product),
          index,
          item_list_name: 'search results',
          item_list_id: 'odoscope',
        }),
      )

      trackViewSearchResult({
        searchTerm: term.value,
        numSearchResults: resultsCount.value,
        ecommerceItems,
      })

      if (loadedProducts.length) {
        trackViewItemList({
          type: 'search_results',
          name: `search: ${term.value}`,
          items: loadedProducts.map(
            (product: Product, index: number) =>
              ({ ...product, index }) as Product & { index: number },
          ),
          filters: '',
          filterOptions: '',
          sorting: '',
          totalItemQuantity: resultsCount.value,
          totalPages: odoscopeSearch.pagination.value?.last ?? 1,
          viewItemQuantity: loadedProducts.length,
          currentPage: odoscopeSearch.page.value,
        })
      }
    },
    { immediate: true },
  )
})

await fetchOnNavigation(async () => {
  await odoscopeSearch.fetchProducts()
})

/** SEO */

const metaTags = metaTagGenerator({
  robots: 'noindex,nofollow',
})

const metaLinks = useStaticMetaLinks('search')

useSeoMeta({
  robots: 'noindex,nofollow',
})

useHead({
  ...metaTags,
  title: headline.value,
  link: [...(metaTags.link || []), ...metaLinks.value],
})
</script>
